"use client";

/**
 * P3-09 · Guest localStorage toggle-set (mirrors the P3-15 recent-searches
 * store). A per-key set of product ids that persists across reloads and syncs
 * across tabs. SSR-safe: the server renders empty and hydration reads
 * localStorage only after mount. Used directly for compare / save-for-later
 * (client-only this phase) and as the guest half of {@link useWishlist}.
 *
 * Per-origin storage is already per-brand (each brand is its own host), so no
 * brand namespacing in the key.
 */
import { useCallback, useEffect, useState } from "react";

function read(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Private mode / quota — best-effort, never fatal.
  }
}

/** Read a guest set directly (for the sign-in merge). */
export function readGuestSet(key: string): string[] {
  return read(key);
}

/** Clear a guest set (after merging it into the account). */
export function clearGuestSet(key: string): void {
  if (typeof window === "undefined") return;
  write(key, []);
}

export interface ToggleSet {
  items: string[];
  has: (id: string) => boolean;
  /** Flip membership; returns the new saved state. */
  toggle: (id: string) => boolean;
  clear: () => void;
}

export function useLocalToggleSet(key: string, enabled = true): ToggleSet {
  const [items, setItems] = useState<string[]>([]);

  // Hydrate after mount (SSR renders empty). Reflect other-tab writes.
  useEffect(() => {
    if (!enabled) return;
    setItems(read(key));
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setItems(read(key));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, enabled]);

  const has = useCallback((id: string) => items.includes(id), [items]);

  const toggle = useCallback(
    (id: string) => {
      // localStorage is the source of truth — read/compute/write/reflect so the
      // returned state is reliable regardless of a stale render closure.
      const current = read(key);
      const exists = current.includes(id);
      const next = exists ? current.filter((x) => x !== id) : [id, ...current];
      write(key, next);
      setItems(next);
      return !exists;
    },
    [key],
  );

  const clear = useCallback(() => {
    setItems([]);
    write(key, []);
  }, [key]);

  return { items, has, toggle, clear };
}
